import * as Cesium from "cesium";
import { Season } from "@/stores/solarSimulationStore";

export interface SolarAnchor {
  latitude: number;
  longitude: number;
}

export interface SolarLightingCurve {
  elevation: number;
  azimuth: number;
  isAboveHorizon: boolean;
  lightDirection: Cesium.Cartesian3;
  daylightFactor: number;
  twilightFactor: number;
  sceneLightIntensityFactor: number;
}

/**
 * Calcule la déclinaison solaire approximative pour une saison donnée.
 * La déclinaison varie entre -23.44° (solstice d'hiver) et +23.44° (solstice d'été)
 */
function getDeclinationForSeason(season: Season): number {
  switch (season) {
    case Season.SPRING:
      return 0; // Équinoxe de printemps
    case Season.SUMMER:
      return 23.44; // Solstice d'été
    case Season.AUTUMN:
      return 0; // Équinoxe d'automne
    case Season.WINTER:
      return -23.44; // Solstice d'hiver
  }
}

/**
 * Calcule l'angle d'élévation du soleil (angle par rapport à l'horizon)
 * en fonction de l'heure du jour et de la déclinaison solaire.
 *
 * Formule simplifiée (sans corrections géographiques exactes):
 * - À midi (heure solaire = 12), l'angle est maximal
 * - L'angle décroît au lever/coucher du soleil
 */
function calculateSunElevation(
  hour: number,
  declination: number,
  latitude: number = 46.8, // Latitude moyenne Suisse
): number {
  // Heure solaire approximative (0-24)
  const hourAngle = (hour - 12) * 15; // 15° par heure

  // Formule simplifiée pour l'angle d'élévation
  // altitude = arcsin(sin(lat) * sin(dec) + cos(lat) * cos(dec) * cos(hourAngle))
  const latRad = Cesium.Math.toRadians(latitude);
  const decRad = Cesium.Math.toRadians(declination);
  const hourRad = Cesium.Math.toRadians(hourAngle);

  const elevation = Math.asin(
    Math.sin(latRad) * Math.sin(decRad) +
      Math.cos(latRad) * Math.cos(decRad) * Math.cos(hourRad),
  );

  return Cesium.Math.toDegrees(elevation);
}

/**
 * Calcule l'azimut du soleil (direction: 0°=Nord, 90°=Est, 180°=Sud, 270°=Ouest)
 * en fonction de l'heure du jour et de la déclinaison solaire.
 */
function calculateSunAzimuth(
  hour: number,
  declination: number,
  latitude: number = 46.8,
): number {
  const latRad = Cesium.Math.toRadians(latitude);
  const decRad = Cesium.Math.toRadians(declination);
  const hourAngle = (hour - 12) * 15;
  const hourRad = Cesium.Math.toRadians(hourAngle);

  // Convention de sortie: 0°=Nord, 90°=Est, 180°=Sud, 270°=Ouest.
  // Cette forme évite l'inversion Est/Ouest observée avec la formule précédente.
  const azimuth = Math.atan2(
    -Math.sin(hourRad),
    Math.tan(decRad) * Math.cos(latRad) -
      Math.sin(latRad) * Math.cos(hourRad),
  );

  return ((Cesium.Math.toDegrees(azimuth) % 360) + 360) % 360;
}

/**
 * Crée une position 3D pour la lumière du soleil dans Cesium
 * Direction opposée à celle du soleil (la lumière vient du soleil vers nous)
 */
export function createSunLightDirection(
  hour: number,
  season: Season,
  anchor: SolarAnchor = { latitude: 46.8, longitude: 8.3 },
): Cesium.Cartesian3 {
  const declination = getDeclinationForSeason(season);
  const elevation = calculateSunElevation(hour, declination, anchor.latitude);
  const azimuth = calculateSunAzimuth(hour, declination, anchor.latitude);

  // Construire d'abord un vecteur local ENU (Est, Nord, Haut).
  const elevationRad = Cesium.Math.toRadians(elevation);
  const azimuthRad = Cesium.Math.toRadians(azimuth);

  // Vecteur pointant VERS le soleil
  const x = Math.cos(elevationRad) * Math.sin(azimuthRad);
  const y = Math.cos(elevationRad) * Math.cos(azimuthRad);
  const z = Math.sin(elevationRad);

  // Normaliser et inverser pour que ça pointe DEPUIS le soleil
  const directionLocale = Cesium.Cartesian3.normalize(
    new Cesium.Cartesian3(-x, -y, -z),
    new Cesium.Cartesian3(),
  );

  // Convertir le vecteur local vers le repère monde Cesium (ECEF).
  const origine = Cesium.Cartesian3.fromDegrees(anchor.longitude, anchor.latitude, 0);
  const repereEnu = Cesium.Transforms.eastNorthUpToFixedFrame(origine);
  const rotationEnuVersMonde = Cesium.Matrix4.getMatrix3(
    repereEnu,
    new Cesium.Matrix3(),
  );

  return Cesium.Cartesian3.normalize(
    Cesium.Matrix3.multiplyByVector(
      rotationEnuVersMonde,
      directionLocale,
      new Cesium.Cartesian3(),
    ),
    new Cesium.Cartesian3(),
  );
}

/**
 * Retourne un objet avec les informations du soleil
 * utiles pour déboguer ou afficher à l'utilisateur
 */
export function calculateSolarPosition(
  hour: number,
  season: Season,
  anchor?: SolarAnchor,
): SolarLightingCurve {
  const declination = getDeclinationForSeason(season);
  const latitude = anchor?.latitude ?? 46.8;
  const elevation = calculateSunElevation(hour, declination, latitude);
  const azimuth = calculateSunAzimuth(hour, declination, latitude);
  const lightDirection = createSunLightDirection(hour, season, anchor);
  const daylightFactor = Cesium.Math.clamp((elevation + 6) / 18, 0, 1);
  const twilightFactor = Cesium.Math.clamp((elevation + 12) / 24, 0, 1);
  const sceneLightIntensityFactor = Cesium.Math.lerp(
    0.08,
    1,
    daylightFactor * daylightFactor,
  );

  return {
    elevation,
    azimuth,
    isAboveHorizon: elevation > 0,
    lightDirection,
    daylightFactor,
    twilightFactor,
    sceneLightIntensityFactor,
  };
}
